import { createContext, useContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api';
import { useAuth } from './AuthContext';

/**
 * Attorney Notepad — case-scoped clip + notebook state.
 *
 * A "clip" is one thing an attorney flagged as important: a highlighted
 * passage, a saved Arc answer, or a freeform note. Clips are shared across the
 * case team (author attribution on each) and deep-link back to their source.
 *
 * Clips are organized into **notebooks** — named containers the attorney can
 * navigate between and export independently. Every case has a default "Notes"
 * notebook; new clips land in the *active* notebook. The full clip set stays
 * loaded (so document overlays + inventory badges count across all notebooks);
 * the drawer filters the visible set to the active notebook client-side.
 *
 * See ``plans/MULTI_NOTEPAD_PLAN.md``. Backend:
 * ``/api/v1/cases/{caseId}/annotations`` + ``/notebooks``.
 *
 * useNotepad() returns null when no provider is mounted, so components that may
 * render outside a case (e.g. ChatMessage in other surfaces) can no-op safely.
 */
const NotepadContext = createContext(null);

const activeKey = (caseId) => `cs:notepad:${caseId}:activeNotebook`;

export function NotepadProvider({ caseId, children }) {
  // Gate fetches on auth: this provider mounts OUTSIDE ProtectedRoute and gets
  // caseId straight from the URL, so on a fresh load that lands directly on a
  // /case/:id route (deep-link, or step-up re-auth return) its effects would
  // otherwise fire `/annotations` + `/notebooks` while AuthContext is still
  // initializing the token — token-less → 401 → forced "session expired"
  // logout. Waiting for `user` ensures the token is in place first.
  const { user } = useAuth();
  const [clips, setClips] = useState([]);
  const [notebooks, setNotebooks] = useState([]);
  const [activeNotebookId, setActiveNotebookId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [filterMine, setFilterMine] = useState(false);
  // Org member directory for the share picker (lazily loaded on first need).
  const [orgMembers, setOrgMembers] = useState([]);
  // Case-wide tag vocabulary [{tag, count}] — feeds autocomplete + facet chips.
  const [caseTags, setCaseTags] = useState([]);
  // id of a clip just created via capture — the drawer auto-focuses its note
  // field so the attorney can "highlight → quick note" in one motion.
  const [justAddedId, setJustAddedId] = useState(null);
  const seq = useRef(0);

  const refresh = useCallback(async () => {
    if (!caseId) return;
    const ticket = ++seq.current;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/cases/${caseId}/annotations`);
      if (ticket !== seq.current) return; // a newer refresh superseded us
      setClips(res?.annotations || []);
    } catch (err) {
      if (ticket === seq.current) setError(err.message || 'Failed to load notepad');
    } finally {
      if (ticket === seq.current) setLoading(false);
    }
  }, [caseId]);

  // Load the notebook list (the server creates the default on first read).
  const refreshNotebooks = useCallback(async () => {
    if (!caseId) return;
    try {
      const res = await api.get(`/cases/${caseId}/notebooks`);
      setNotebooks(res?.notebooks || []);
    } catch (err) {
      setError(err.message || 'Failed to load notebooks');
    }
  }, [caseId]);

  // Case-wide tag vocabulary + counts (authoritative source for autocomplete +
  // facet chips; spans every notebook the viewer can see).
  const refreshCaseTags = useCallback(async () => {
    if (!caseId) return;
    try {
      const res = await api.get(`/cases/${caseId}/annotations/tags`);
      setCaseTags(res?.tags || []);
    } catch { /* non-fatal — chips fall back to loaded-clip tags */ }
  }, [caseId]);

  useEffect(() => { if (user) refresh(); }, [refresh, user]);
  useEffect(() => { if (user) refreshNotebooks(); }, [refreshNotebooks, user]);
  useEffect(() => { if (user) refreshCaseTags(); }, [refreshCaseTags, user]);

  // Pick the active notebook once the list loads: a remembered choice that
  // still exists wins, else the case default, else the first notebook.
  useEffect(() => {
    if (!caseId || notebooks.length === 0) return;
    setActiveNotebookId((cur) => {
      if (cur && notebooks.some(n => n.id === cur)) return cur;
      let remembered = null;
      try { remembered = localStorage.getItem(activeKey(caseId)); } catch { /* ignore */ }
      if (remembered && notebooks.some(n => n.id === remembered)) return remembered;
      const def = notebooks.find(n => n.is_default) || notebooks[0];
      return def?.id || null;
    });
  }, [caseId, notebooks]);

  const setActiveNotebook = useCallback((id) => {
    setActiveNotebookId(id);
    try { localStorage.setItem(activeKey(caseId), id); } catch { /* ignore */ }
  }, [caseId]);

  // Live refresh: while the drawer is open, re-pull clips on open + every 60s
  // so a teammate's new clips appear without a page reload. Polling is gated
  // to the open drawer — no background load when it's closed.
  useEffect(() => {
    if (!drawerOpen) return undefined;
    refresh();
    refreshNotebooks();
    const id = setInterval(() => { refresh(); refreshNotebooks(); }, 60000);
    return () => clearInterval(id);
  }, [drawerOpen, refresh, refreshNotebooks]);

  /** Create a clip in the active notebook. Returns the created clip (or null). */
  const addClip = useCallback(async (payload) => {
    if (!caseId) return null;
    try {
      const body = { ...payload };
      if (body.notebook_id == null && activeNotebookId) body.notebook_id = activeNotebookId;
      const clip = await api.post(`/cases/${caseId}/annotations`, body);
      // Prepend; pinned-first ordering is re-applied by the server on refresh.
      setClips(prev => [clip, ...prev]);
      return clip;
    } catch (err) {
      setError(err.message || 'Failed to save clip');
      return null;
    }
  }, [caseId, activeNotebookId]);

  const updateClip = useCallback(async (id, patch) => {
    if (!caseId) return null;
    try {
      const updated = await api.patch(`/cases/${caseId}/annotations/${id}`, patch);
      setClips(prev => prev.map(c => (c.id === id ? updated : c)));
      if ('tags' in patch) refreshCaseTags();  // tag vocabulary may have changed
      return updated;
    } catch (err) {
      setError(err.message || 'Failed to update clip');
      return null;
    }
  }, [caseId, refreshCaseTags]);

  /** Move a clip to another notebook (file it elsewhere). Author-only. */
  const moveClip = useCallback((id, notebookId) => (
    updateClip(id, { notebook_id: notebookId })
  ), [updateClip]);

  /**
   * Copy a clip into another notebook. Unlike move, this creates a NEW clip
   * (authored by the current user, so they can manage it) with the same
   * content — so any team member can copy any clip. Returns the new clip.
   */
  const copyClip = useCallback((id, notebookId) => {
    const src = clips.find(c => c.id === id);
    if (!src) return Promise.resolve(null);
    return addClip({
      kind: src.kind,
      document_id: src.document_id,
      notebook_id: notebookId,
      title: src.title,
      anchor: src.anchor,
      quote: src.quote,
      note: src.note,
      citations: src.citations,
      conversation_id: src.conversation_id,
      message_id: src.message_id,
      color: src.color,
      tags: src.tags,
    });
  }, [clips, addClip]);

  const deleteClip = useCallback(async (id) => {
    if (!caseId) return;
    // Optimistic removal — soft-delete on the server, so a failed call just
    // re-appears on next refresh.
    setClips(prev => prev.filter(c => c.id !== id));
    try {
      await api.del(`/cases/${caseId}/annotations/${id}`);
      refreshCaseTags();  // a removed clip may retire its last-used tags
    } catch (err) {
      setError(err.message || 'Failed to delete clip');
      refresh();
    }
  }, [caseId, refresh, refreshCaseTags]);

  /** Capture a clip and open the drawer focused on its note field. */
  const captureClip = useCallback(async (payload) => {
    const clip = await addClip(payload);
    if (clip) {
      setJustAddedId(clip.id);
      setDrawerOpen(true);
    }
    return clip;
  }, [addClip]);

  // ── Notebook mutators ───────────────────────────────────────────────
  const createNotebook = useCallback(async ({ name, color, visibility } = {}) => {
    if (!caseId || !name?.trim()) return null;
    try {
      const nb = await api.post(`/cases/${caseId}/notebooks`, {
        name: name.trim(), color, visibility: visibility || 'organization',
      });
      setNotebooks(prev => [...prev, nb]);
      setActiveNotebook(nb.id);
      return nb;
    } catch (err) {
      setError(err.message || 'Failed to create notebook');
      return null;
    }
  }, [caseId, setActiveNotebook]);

  const renameNotebook = useCallback(async (id, patch) => {
    if (!caseId) return null;
    try {
      const nb = await api.patch(`/cases/${caseId}/notebooks/${id}`, patch);
      setNotebooks(prev => prev.map(n => (n.id === id ? nb : n)));
      return nb;
    } catch (err) {
      setError(err.message || 'Failed to update notebook');
      return null;
    }
  }, [caseId]);

  const deleteNotebook = useCallback(async (id) => {
    if (!caseId) return;
    const target = notebooks.find(n => n.id === id);
    if (target?.is_default) return; // default is protected
    // Optimistic: drop the tab; its clips move to the default server-side, so
    // re-pull clips to reflect the reassignment.
    setNotebooks(prev => prev.filter(n => n.id !== id));
    if (activeNotebookId === id) {
      const def = notebooks.find(n => n.is_default) || notebooks.find(n => n.id !== id);
      if (def) setActiveNotebook(def.id);
    }
    try {
      await api.del(`/cases/${caseId}/notebooks/${id}`);
      refresh();
    } catch (err) {
      setError(err.message || 'Failed to delete notebook');
      refreshNotebooks();
    }
  }, [caseId, notebooks, activeNotebookId, setActiveNotebook, refresh, refreshNotebooks]);

  /** Change a notebook's visibility scope (author only). */
  const setNotebookVisibility = useCallback((id, visibility) => (
    renameNotebook(id, { visibility })
  ), [renameNotebook]);

  /** Fetch a notebook's member grants (author only). */
  const getNotebookShares = useCallback(async (id) => {
    if (!caseId) return null;
    try {
      return await api.get(`/cases/${caseId}/notebooks/${id}/shares`);
    } catch (err) {
      setError(err.message || 'Failed to load shares');
      return null;
    }
  }, [caseId]);

  /**
   * Replace a notebook's member grants. ``shares`` is [{user_id, permission}].
   * Adding members flips the notebook to "shared" server-side; we re-pull the
   * notebook list so the tab badge reflects the new scope.
   */
  const setNotebookShares = useCallback(async (id, shares) => {
    if (!caseId) return null;
    try {
      const res = await api.put(`/cases/${caseId}/notebooks/${id}/shares`, { shares });
      await refreshNotebooks();
      return res;
    } catch (err) {
      setError(err.message || 'Failed to update shares');
      return null;
    }
  }, [caseId, refreshNotebooks]);

  /** Load the org member directory (cached after first fetch) for the picker. */
  const loadOrgMembers = useCallback(async () => {
    if (orgMembers.length > 0) return orgMembers;
    try {
      const res = await api.get('/org/members');
      const members = res?.members || [];
      setOrgMembers(members);
      return members;
    } catch (err) {
      setError(err.message || 'Failed to load members');
      return [];
    }
  }, [orgMembers]);

  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const toggleDrawer = useCallback(() => setDrawerOpen(o => !o), []);
  const clearJustAdded = useCallback(() => setJustAddedId(null), []);

  /** Open the drawer focused on a specific clip (e.g. clicking its overlay). */
  const openClip = useCallback((id) => {
    // Jump to the clip's notebook so it's actually visible in the filtered list.
    const clip = clips.find(c => c.id === id);
    if (clip?.notebook_id && clip.notebook_id !== activeNotebookId) {
      setActiveNotebook(clip.notebook_id);
    }
    setJustAddedId(id);
    setDrawerOpen(true);
  }, [clips, activeNotebookId, setActiveNotebook]);

  const value = useMemo(() => ({
    caseId,
    clips,
    count: clips.length,
    notebooks,
    activeNotebookId,
    loading,
    error,
    drawerOpen,
    filterMine,
    justAddedId,
    refresh,
    addClip,
    updateClip,
    moveClip,
    copyClip,
    deleteClip,
    captureClip,
    setActiveNotebook,
    createNotebook,
    renameNotebook,
    deleteNotebook,
    setNotebookVisibility,
    getNotebookShares,
    setNotebookShares,
    loadOrgMembers,
    orgMembers,
    caseTags,
    refreshCaseTags,
    currentUserId: user?.uid || null,
    openDrawer,
    closeDrawer,
    toggleDrawer,
    openClip,
    setFilterMine,
    clearJustAdded,
  }), [
    caseId, clips, notebooks, activeNotebookId, loading, error, drawerOpen,
    filterMine, justAddedId, refresh, addClip, updateClip, moveClip, copyClip, deleteClip,
    captureClip, setActiveNotebook, createNotebook, renameNotebook, deleteNotebook,
    setNotebookVisibility, getNotebookShares, setNotebookShares, loadOrgMembers,
    orgMembers, caseTags, refreshCaseTags, user, openDrawer, closeDrawer,
    toggleDrawer, openClip, clearJustAdded,
  ]);

  return (
    <NotepadContext.Provider value={value}>
      {children}
    </NotepadContext.Provider>
  );
}

/** Returns the notepad API, or null when rendered outside a NotepadProvider. */
export function useNotepad() {
  return useContext(NotepadContext);
}
